import { Button, Card, CardHeader, EmptyState, Skeleton } from "@helmr/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { logout } from "../../api/auth";
import { isApiError } from "../../api/client";
import { deleteKiosk } from "../../api/kiosk";
import { useAppContext } from "../../app/permissions";
import { useServices } from "../../app/services";
import { kioskHost } from "./host";
import s from "./kiosk.module.css";

export const exitStrings = {
  title: "Stop using this device as a kiosk",
  body: "The check-in screen goes away and this device becomes a normal Helmr device. Scans still waiting to sync are lost, so go online first if it shows any.",
  revoke: "Also revoke this kiosk for good",
  revokeHint: "It stops working everywhere and cannot be paired again. A scanner kiosk also makes the gym's check-in key change.",
  confirm: "Stop using as kiosk",
  busy: "Stopping…",
  back: "Back to the kiosk",
  none: "This device is not a kiosk",
  noneBody: "Pair it from Settings → Devices.",
  noneAction: "Pair this device",
  noEdit: "You can't change kiosks",
  noEditBody: "Stopping kiosk mode needs permission to edit devices. Ask the owner.",
} as const;

export function KioskExit() {
  const { client, session, queryClient } = useServices();
  const ctx = useAppContext();
  const navigate = useNavigate();
  const host = kioskHost();
  const cred = useQuery({ queryKey: ["kiosk", "credential"], queryFn: () => host.store.read("credential").catch(() => null), staleTime: 0, gcTime: 0, retry: false });
  const [revoke, setRevoke] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Staff never stay signed in on a kiosk: leave the same way pairing enters. */
  async function backToKiosk() {
    setBusy(true);
    await logout(client).catch(() => undefined);
    session.signOut(null);
    queryClient.clear();
    host.assign("/kiosk");
  }

  if (ctx.status === "loading" || cred.isPending) return <Card><div style={{ padding: 16 }} aria-busy="true"><Skeleton height={20} width="40%" /></div></Card>;
  if (!cred.data) {
    return <Card><EmptyState icon="finger" title={exitStrings.none} description={exitStrings.noneBody} action={<Button as={Link} to="/kiosk/setup">{exitStrings.noneAction}</Button>} /></Card>;
  }
  if (!ctx.has("devices.kiosks_edit")) {
    return (
      <Card><EmptyState icon="lock" title={exitStrings.noEdit} description={exitStrings.noEditBody}
        action={<Button onClick={() => void backToKiosk()} busy={busy} busyLabel="Leaving…">{exitStrings.back}</Button>} /></Card>
    );
  }
  const credential = cred.data;
  const canRevoke = ctx.has("devices.kiosk_revoke");

  async function stop() {
    setBusy(true);
    setError(null);
    try {
      if (revoke && canRevoke) {
        try { await deleteKiosk(client, credential.kioskId); }
        catch (e) { if (!(isApiError(e) && e.status === 409)) throw e; /* already revoked: nothing left to do */ }
      }
      await host.store.wipe();
      queryClient.removeQueries({ queryKey: ["kiosk"] });
      navigate("/settings/devices", { replace: true });
    } catch (e) {
      setError(isApiError(e) ? e.message : "Could not stop kiosk mode. Try again.");
      setBusy(false);
    }
  }

  return (
    <div className={s.page}>
      <div className={s.head}><h1>{exitStrings.title}</h1><p>{`${credential.label} · ${credential.mode === "scanner" ? "Scanner" : "Wall code"}`}</p></div>
      <Card>
        <CardHeader title="Leave kiosk mode" />
        <div className={s.form}>
          <p>{exitStrings.body}</p>
          {canRevoke ? (
            <label className={s.check}><input type="checkbox" checked={revoke} onChange={(e) => setRevoke(e.target.checked)} /><span><b>{exitStrings.revoke}</b><br /><span className={s.hint}>{exitStrings.revokeHint}</span></span></label>
          ) : null}
          {error ? <p className={s.formErr} role="alert">{error}</p> : null}
          <div className={s.bar}>
            <Button variant="primary" onClick={() => void stop()} busy={busy} busyLabel={exitStrings.busy}>{exitStrings.confirm}</Button>
            <Button onClick={() => void backToKiosk()} disabled={busy}>{exitStrings.back}</Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
